/**
 * Inline best/worst scenario deals for a hospital — shown on the
 * consumer search page when a hospital row is expanded.
 *
 * Wraps useHospitalScenarios and renders a single horizontal scrolling
 * rail of ScenarioCards (mode-driven: best deals OR most expensive)
 * with optional category filter pills matching the map bottom panel.
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2, TrendingDown, TrendingUp, ChevronLeft, ChevronRight,
  Flame, Bone, Activity, Scissors, Baby, Heart, Ribbon,
  Shield, Pill, Wind, Dna, Replace,
} from 'lucide-react';
import { useHospitalScenarios, type HospitalScenarioRow } from '@/hooks/useHospitalScenarios';
import { ScenarioCard, type ScenarioCardData, type CardTheme } from '@/components/ScenarioCard';
import { SCENARIO_CATEGORIES, scenarioMatchesCategory } from '@/config/scenarioCategories';

/** Icons for scenario categories (same as map bottom panel) */
const CATEGORY_ICONS: Record<string, any> = {
  emergency: Flame, orthopedic: Bone, spine: Activity, surgery: Scissors,
  'womens-health': Baby, cardiology: Heart, cancer: Ribbon, immune: Shield,
  diabetes: Pill, pulmonology: Wind, transplant: Replace, 'gene-therapy': Dna,
};

function toCardData(row: HospitalScenarioRow, accentColor: string): ScenarioCardData {
  const s = row.scenario;
  return {
    id: row.scenario_id,
    name: s.name,
    description: s.description || '',
    icon_name: s.icon_name || 'Activity',
    // Accent color for the scenario icon + its tinted backdrop. Sourced from
    // the parent-passed theme so AHP gets navy, Luigi's gets matrix-green,
    // AntiGreed gets cream — no brand bleed-through from a hardcoded patriotic
    // navy on a Luigi's-skinned page.
    color: accentColor,
    duration: s.duration || '',
    total_procedures: s.total_procedures || 0,
    phases: s.phases || [],
    severity_level: s.severity_level,
    calculated_avg_cost: row.nationalCost || s.estimated_cost || 0,
    phaseBreakdown: s.phaseBreakdown || {},
    phaseProcedures: {},
    tags: s.tags || [],
    created_by: null,
    is_verified: true,
  };
}

function toHospitalOverride(row: HospitalScenarioRow, hospitalId?: string) {
  return {
    blendedPrice: row.blendedPrice,
    blendedMarkup: row.blendedMarkup,
    matchedCodes: row.matched_codes,
    totalCodes: row.total_codes,
    nationalCost: row.nationalCost,
    hospitalId,
  };
}

interface Props {
  hospitalId: string;
  theme: CardTheme;
  /** Which rail to show. Parent controls this; component shows one rail at a time. */
  mode?: 'deal' | 'expensive';
}

export function HospitalScenarioDeals({ hospitalId, theme, mode = 'deal' }: Props) {
  const { t } = useTranslation('scenarios');
  // Lazy-load: only fetch scenario data when the component scrolls into view
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' } // start loading 200px before visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!isVisible) {
    return (
      <div ref={sentinelRef} style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 12, color: theme.textMuted }}>{t('deals.scrollToLoad')}</span>
      </div>
    );
  }

  return <HospitalScenarioDealsInner hospitalId={hospitalId} theme={theme} mode={mode} />;
}

function HospitalScenarioDealsInner({ hospitalId, theme, mode = 'deal' }: Props) {
  const { t } = useTranslation('scenarios');
  const { cheapestAll, mostExpensiveAll, byCategory, totalCount, isLoading } = useHospitalScenarios(hospitalId);
  const [bookmarked, setBookmarked] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const toggleBookmark = (id: string) => {
    const next = new Set(bookmarked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setBookmarked(next);
  };

  // Build display rows based on mode + category filter.
  // Each mode only shows rows that MATCH its promise: "Most Expensive" lists
  // only scenarios priced ABOVE the national average, "Best Deals" only ones
  // BELOW it. (Previously the lists padded with whatever was closest — a
  // −92%-markup hospital showed "5% cheaper" deals under the Most Expensive
  // tab. Honest empty states below replace that.)
  const displayRows = useMemo(() => {
    const fits = (r: { blendedMarkup: number }) => mode === 'deal' ? r.blendedMarkup < 0 : r.blendedMarkup > 0;
    if (categoryFilter) {
      const catRows = (byCategory[categoryFilter] || []).filter(fits);
      return mode === 'deal'
        ? [...catRows].sort((a, b) => a.blendedMarkup - b.blendedMarkup).slice(0, 10)
        : [...catRows].sort((a, b) => b.blendedMarkup - a.blendedMarkup).slice(0, 10);
    }
    return (mode === 'deal' ? cheapestAll : mostExpensiveAll).filter(fits).slice(0, 5);
  }, [mode, categoryFilter, cheapestAll, mostExpensiveAll, byCategory]);

  // Available categories (only ones with data for this hospital)
  const availableCategories = useMemo(() =>
    SCENARIO_CATEGORIES.filter(c => c.key !== 'all' && byCategory[c.key]?.length > 0),
    [byCategory]
  );

  // Labels: the lists are strictly filtered to their promise now, so the rail
  // label is always literal.
  const anyAboveAvg = mostExpensiveAll.some(r => r.blendedMarkup > 0);
  const anyBelowAvg = cheapestAll.some(r => r.blendedMarkup < 0);
  const catSuffix = categoryFilter ? ` — ${SCENARIO_CATEGORIES.find(c => c.key === categoryFilter)?.label || 'Category'}` : '';

  const railLabel = mode === 'deal' ? `${t('deals.bestDeals')}${catSuffix}` : `${t('deals.mostExpensive')}${catSuffix}`;
  const railColor = mode === 'deal' ? '#16a34a' : '#dc2626';
  const RailIcon = mode === 'deal' ? TrendingDown : TrendingUp;
  const priceContext: 'deal' | 'expensive' = mode;
  const railId = `${mode}-rail-${hospitalId}`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" style={{ color: theme.textMuted }}>
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span style={{ fontSize: 13 }}>{t('deals.loading')}</span>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="text-center py-6" style={{ color: theme.textMuted, fontSize: 13 }}>
        {t('deals.noCoverage')}
      </div>
    );
  }

  return (
    <div className="space-y-2 py-3">
      {/* Category filter pills */}
      {availableCategories.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar" style={{ scrollbarWidth: 'none' }}>
          <button
            className="text-[10px] px-2 py-0.5 rounded-full shrink-0 transition-colors"
            style={{
              backgroundColor: !categoryFilter ? theme.text : 'transparent',
              color: !categoryFilter ? theme.cardBg : theme.textMuted,
              border: `1px solid ${!categoryFilter ? theme.text : theme.border}`,
              fontWeight: !categoryFilter ? 600 : 400,
            }}
            onClick={() => setCategoryFilter(null)}
          >
            {t('deals.all')}
          </button>
          {availableCategories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.key] || Activity;
            const isActive = categoryFilter === cat.key;
            return (
              <button
                key={cat.key}
                className="text-[10px] px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1 transition-colors"
                style={{
                  backgroundColor: isActive ? cat.color : 'transparent',
                  color: isActive ? '#fff' : theme.textMuted,
                  border: `1px solid ${isActive ? cat.color : theme.border}`,
                  fontWeight: isActive ? 600 : 400,
                }}
                onClick={() => setCategoryFilter(isActive ? null : cat.key)}
                title={`${cat.browseLabel} (${byCategory[cat.key]?.length || 0})`}
              >
                <Icon className="w-3 h-3" />
                {cat.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Scenario cards rail */}
      {displayRows.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RailIcon className="w-4 h-4" style={{ color: railColor }} />
              <h3 style={{ fontSize: 14, fontWeight: 600, color: railColor, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                {railLabel}
              </h3>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => { const el = document.getElementById(railId); if (el) el.scrollBy({ left: -300, behavior: 'smooth' }); }}
                className="w-7 h-7 rounded-full flex items-center justify-center"
                style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => { const el = document.getElementById(railId); if (el) el.scrollBy({ left: 300, behavior: 'smooth' }); }}
                className="w-7 h-7 rounded-full flex items-center justify-center"
                style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div
            id={railId}
            className="flex gap-3 overflow-x-auto pb-2"
            style={{ scrollbarWidth: 'thin', scrollSnapType: 'x mandatory' }}
          >
            {displayRows.map((row) => (
              <div
                key={row.scenario_id}
                style={{ minWidth: 340, maxWidth: 380, flexShrink: 0, scrollSnapAlign: 'start' }}
              >
                <ScenarioCard
                  scenario={toCardData(row, theme.blue)}
                  compact
                  isStarred={bookmarked.has(row.scenario_id)}
                  theme={theme}
                  onToggleBookmark={toggleBookmark}
                  hospitalOverride={{ ...toHospitalOverride(row, hospitalId), priceContext }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Honest empty states — never pad "Most Expensive" with cheaper-than-
           average scenarios (or vice versa). */
        <div className="text-center py-5 px-4" style={{ color: theme.textMuted, fontSize: 13, lineHeight: 1.5 }}>
          {categoryFilter
            ? t('deals.noneInCategory')
            : mode === 'expensive'
              ? (anyBelowAvg
                  ? t('deals.noneAboveAvg', { defaultValue: 'No tracked scenario at this hospital is priced above the national average — every covered scenario here costs average or less.' })
                  : t('deals.noneAboveAvgPlain', { defaultValue: 'No tracked scenario at this hospital is priced above the national average.' }))
              : t('deals.noneBelowAvg', { defaultValue: 'No tracked scenario at this hospital is priced below the national average.' })}
        </div>
      )}

      <div style={{ fontSize: 11, color: theme.textMuted, textAlign: 'center', fontStyle: 'italic' }}>
        {t('deals.rankedCount', { count: totalCount })}
      </div>
    </div>
  );
}
